import { useScrollNavigation } from '@client/hooks/useScrollNavigation/useScrollNavigation';
import { IconAction, Pill } from '@tkww/orion-web-ds-action';
import type { CategoryScoreRec } from '@typings/api/categoryRec';
import { compose } from '@xo-union/react-css-modules';
import cx from 'classnames';
import React, { useEffect } from 'react';
import { getCategoryTitle } from '../../../lib/mcvrm/utils';
import Styles from './PillsCategoriesCarousel.scss';

interface PillsCategoriesCarouselProps {
	categories: CategoryScoreRec[];
	selectedCategoryCode: string | null;
	onCategoryClick: (category: string) => void;
	className?: string;
}

export const PillsCategoriesCarousel = ({
	categories,
	selectedCategoryCode,
	onCategoryClick,
	className = '',
}: PillsCategoriesCarouselProps) => {
	const {
		trackRef,
		canScrollLeft,
		canScrollRight,
		scrollLeft,
		scrollRight,
		scrollToElement,
		onScroll,
	} = useScrollNavigation();

	useEffect(() => {
		if (selectedCategoryCode) {
			scrollToElement(`[data-mcvrm-category-code="${selectedCategoryCode}"]`);
		}
	}, [selectedCategoryCode, scrollToElement]);

	return (
		<div className={cx(Styles.pillContainer, className)}>
			{canScrollLeft && (
				<div className={cx(Styles.navButton, Styles.navButtonLeft)}>
					<IconAction
						name="carousel/prev"
						size="md"
						aria-label="See previous pills"
						onClick={scrollLeft}
						className={Styles.navButtonIcon}
					/>
				</div>
			)}
			<div ref={trackRef} className={Styles.pillTrack} onScroll={onScroll}>
				{categories.map(({ category }) => {
					return (
						<Pill
							key={category}
							onClick={() => onCategoryClick(category)}
							classes={compose({
								container: Styles.pillItemContainer,
								content: Styles.pillContent,
							})}
							className={cx(Styles.pill, {
								[Styles.pillActive]: selectedCategoryCode === category,
							})}
							data-testid={category}
							data-mcvrm-category-code={category}
						>
							{getCategoryTitle(category)}
						</Pill>
					);
				})}
			</div>
			{canScrollRight && (
				<div className={cx(Styles.navButton, Styles.navButtonRight)}>
					<IconAction
						name="carousel/next"
						size="md"
						aria-label="See next pills"
						onClick={scrollRight}
						className={Styles.navButtonIcon}
					/>
				</div>
			)}
		</div>
	);
};
